import * as React from 'react';

import {
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_EMBED,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_TABLE,
    BLOCK_TABS,
} from '@gitbook/doc-core';
import { PageLayoutOptionsWidth } from '@gitbook/hive-core';
import type { PaletteGroupDef } from '@gitbook/react-uikit';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { ContentEditorContext } from '../../contexts';
import { PageLayoutWidthContext } from '../../PageLayout';

export const FULL_WIDTH_BLOCK_TYPES = [
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_EMBED,
    BLOCK_INTEGRATION,
    BLOCK_IMAGES,
    BLOCK_TABLE,
    BLOCK_TABS,
];

/**
 * Hook to get layout commands for a block for ex: `full width` or `default width`
 */
export function useBlockLayoutGroupFactory(input: {
    nodeKey: string;
}): () => PaletteGroupDef | null {
    const { nodeKey } = input;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const pageWidth = React.useContext(PageLayoutWidthContext);

    const editor = useEditor();
    const block = editor.value.document.assertBlock(nodeKey);

    return useEventCallback(() => {
        if (
            pageWidth === PageLayoutOptionsWidth.Wide ||
            !FULL_WIDTH_BLOCK_TYPES.includes(block.type)
        ) {
            return null;
        }

        return {
            key: 'layout-width',
            commands: [
                block.data.get('fullWidth') === true
                    ? {
                          key: 'default',
                          title: 'Default width',
                          icon: Icon.DefaultWidth,
                          trigger: () => {
                              editor.change((change) => {
                                  change.setDataByKey(nodeKey, ['fullWidth'], false);
                              });
                              logAnalyticsEvent({
                                  type: 'edit_page_document_edit_block_property',
                                  blockType: block.type,
                                  blockProperty: 'layout',
                                  fromValue: 'full',
                                  toValue: 'default',
                              });
                          },
                      }
                    : {
                          key: 'full',
                          title: 'Full width',
                          icon: Icon.FullWidth,
                          trigger: () => {
                              editor.change((change) => {
                                  change.setDataByKey(nodeKey, ['fullWidth'], true);
                              });
                              logAnalyticsEvent({
                                  type: 'edit_page_document_edit_block_property',
                                  blockType: block.type,
                                  blockProperty: 'layout',
                                  fromValue: 'default',
                                  toValue: 'full',
                              });
                          },
                      },
            ],
        };
    });
}
